<template>
  <div id="container-ca-datasheet">
    <hot-table :settings="settings" ref="ca_datasheet"> </hot-table>
    <v-progress-linear
      v-if="loading_inicial_data && is_loading"
      :indeterminate="true"
    ></v-progress-linear>
  </div>
</template>

<script>
import { HotTable } from '@handsontable/vue';
import 'handsontable/languages/es-MX';
import { TIPO_COLUMNA_TEMPLATE } from './ConfiguracionColumnas';

const MAX_ALTURA_TABLA = 500;

export default {
  components: {
    HotTable,
  },
  props: {
    headers: { type: Array, default: () => [] },
    configuracion_columnas: { type: Array, default: () => [] },
    columnas_fijadas_izquierda: { type: Number, default: 0 },
    fila_totalizadora: { type: Boolean, default: false },
    data: { type: Array, default: () => [] }, // Valores de cada celda de la tabla, accedidos utilizando key de cada ConfiguracionColumnas
    max_height: { type: Number, default: MAX_ALTURA_TABLA }, // Altura máxima de la tabla (si tuviese menos filas no se completa)
    uniones_celdas: { default: false }, // Array con celdas mergeadas o false en caso de no haber uniones entre celdas
    forzar_renderizado: { type: Boolean, default: false }, // Se vuelve a renderizar la tabla al pasar a true.
    columnas_resizeables: { type: Boolean, default: false }, // El ancho de las columnas se puede ajustar manualmente
    columna_extra: { type: Boolean, default: false }, // Se agrega una columna al final para completar el ancho del div que contenga la tabla
    validar: { type: Boolean, default: false }, // Al pasar a true se ejecuta validator de cada columna
    error: { type: Boolean, default: false }, // Para .sync (actualizado al pasar prop validar a true)
    loading_inicial_data: { type: Boolean, default: false }, // Si es true, agrega un linear progress mientras carga la tabla
    usar_hot_columns: { type: Boolean, default: true }, // Uso de hot columns para poder definir custom renderers.
    columnas_disabled: { type: Array, default: () => []}, // Indices de columnas a deshabilitar, con restricción por filas (siguiente prop)
    max_fila_disabled: { type: Number, default: 0}, // Maxima fila a deshabilitar para las columnas indicadas en columnas_disabled
  },
  data: function () {
    return {
      TIPO_COLUMNA_TEMPLATE: TIPO_COLUMNA_TEMPLATE,
      is_loading: true,
      settings: {
        autoRowSize: false,
        autoColumnSize: false,
        licenseKey: 'non-commercial-and-evaluation', // WARNING - ToDo ver qué hacer con esto
        colHeaders: this.headers,
        columnHeaderHeight: 44,
        className: 'ca-datasheet',
        manualColumnResize: this.columnas_resizeables,
        rowHeights: 34,
        colWidths: this.anchosColumnas,
        height: MAX_ALTURA_TABLA,
        stretchH: 'none',
        fixedColumnsLeft: this.columnas_fijadas_izquierda,
        fixedRowsBottom: this.fila_totalizadora ? 1 : 0,
        minSpareCols: 1,
        language: 'es-MX',
        mergeCells: false,
        data: this.data,
        /**
         * Hooks beforeCopy y beforePaste:
         * - data es matriz con valores a copiar/pegar
         * - coords tiene la información de las coordenadas de la tabla donde se está pegando o desde donde se copia
         *
         * Se utiliza coords en conjunto con las columnas (j) para obtener la configuración de cada columna en particular
         *  y que los métodos getValorCopiar/getValorPegar retornen el valor que corresponde de acuerdo al tipo de columna
         *  (texto, numero, porcentaje, importe).
         */
        beforeCopy: (data, coords) => {
          for (let i = 0; i < data.length; i++) {
            // Filas
            for (let j = 0; j < data[i].length; j++) {
              // Columnas
              data[i][j] = this.configuracion_columnas[
                j + coords[0].startCol
              ].getValorCopiar(data[i][j]);
            }
          }
        },
        beforePaste: (data, coords) => {
          for (let i = 0; i < data.length; i++) {
            // Filas
            for (let j = 0; j < data[i].length; j++) {
              // Columnas
              data[i][j] = this.configuracion_columnas[
                j + coords[0].startCol
              ].getValorPegar(data[i][j]);
            }
          }
        },
        /**
         * Hook de cambios, para saber desde donde se utilice el componente si se editaron los datos de la tabla.
         * Se ignora cuando el origen del cambio es loadData porque representa el cargado inicial de datos y no lo
         *  consideramos como un cambio.
         */
        afterChange: (changes, sources) => {
          if (sources !== 'loadData') {
            this.$emit('change', changes);
          }
        },
        /**
         * Oculta el loading luego de que se cargue la información (No incluye la carga inicial y se considera
         * que debe haberse cargado al menos un dato. Esto ya que el hook afterLoadData se activa varias veces
         * antes que se complete la carga de información.
         */
        afterLoadData: (initialLoad) => {
          if (!initialLoad && this.data.length > 0) {
            this.is_loading = false;
          }
        },
        /**
         * Retorno false para evitar la creacion de nuevas filas al pegar
         */
        beforeCreateRow: () => {
          // Emito un evento para avisar que se excedieron la cantidad de row de la tabla
          this.$emit('paste:rows_excedidas');
          return false;
        },
        /**
         * Retorno false para evitar la creacion de nuevas columnas al pegar
         */
        beforeCreateCol: () => {
          return false;
        },
        /**
         * Configuración de celdas para deshabilitar, de acuerdo a filas/columnas.
         * https://handsontable.com/docs/7.1.1/demo-disabled-editing.html
         */
        cells: (row, col, prop) => {
          let cellProperties = {};
          if (row < this.max_fila_disabled && this.columnas_disabled.includes(col)) {
            cellProperties.editor = false;
          }
          return cellProperties;
        }
      },
    };
  },
  computed: {
    /**
     * Calcula la altura de la tabla de acuerdo a los datos, y si no excede el alto máximo (prop max_height),
     *  la retorna. Si no, retorna alto máximo.
     *
     * Consultado al cambiar los datos de la tabla (watcher data)
     *
     * @returns {number}
     */
    alturaCalculada() {
      let total_datos = this.data.length;
      let alto_datos =
        this.settings.rowHeights * total_datos +
        this.settings.columnHeaderHeight;

      return this.max_height > alto_datos ? alto_datos : this.max_height;
    },
    /**
     * Anchos de las columnas de acuerdo a la configuración.
     *
     * @returns {number[]}
     */
    anchosColumnas() {
      return this.configuracion_columnas.map((config) => config.ancho);
    },
    /**
     * Genera configuración de columnas esperada por Hansontable, a partir del formato estandarizado propio
     *  definido en clase ConfiguracionColumnas.
     *
     * @returns {[]}
     */
    configuracionesHT() {
      let configuraciones_ht = this.configuracion_columnas.map((config) =>
        config.getConfiguracionHT()
      );
      if (this.columna_extra) {
        // Se agrega columna extra para completar el ancho de la tabla
        configuraciones_ht.push({
          type: 'text',
          readOnly: true,
          data: 'ultimaColumna',
        });
      }
      return configuraciones_ht;
    },
  },
  watch: {
    //////////// Cambios en props que modifican settings de HandsOnTable ////////////
    data: {
      immediate: true,
      handler(val) {
        this.settings.data = val;
        let altura_tabla = this.alturaCalculada;
        this.settings.height = altura_tabla;
        if (altura_tabla !== this.max_height) {
          this.settings.height += 1; // FIX overflow
        }
      },
    },
    /**
     * Cambio de headers externo
     * Si hay columna extra se agrega la col vacía.
     */
    headers(val) {
      this.settings.colHeaders = [...val];
      if (this.columna_extra) {
        this.settings.colHeaders.push(' ');
      }
    },
    configuracion_columnas() {
      this.settings.colWidths = this.anchosColumnas;
      if (!this.usar_hot_columns) {
        this.actualizarSettingsColumnas();
      }
    },
    uniones_celdas(val) {
      this.settings.mergeCells = val;
    },
    //////////// Cambios en props que no afectan settings de HandsOnTable ////////////
    forzar_renderizado(val) {
      if (val) {
        this.$refs.ca_datasheet.hotInstance.render();
        this.$emit('update:forzar_renderizado', false);
      }
    },
    validar(val) {
      if (val) {
        // Si se pone en true ejecuta validación y actualiza prop error.
        this.$nextTick(() => {
          // Next tick porque se actualiza la configuración de columnas (watcher configuracion_columnas)
          //  a la vez que se activan las validaciones (asi que se debe esperar para poder tomar la última configuración)
          this.$refs.ca_datasheet.hotInstance.validateCells((valid) => {
            this.$emit('update:error', !valid);
            this.$emit('validacion', true);
          });
        });
      }
    },
  },
  /**
   * Hook mounted(), configuración inicial de tabla y renderización.
   */
  mounted() {
    this.settings.colWidths = this.anchosColumnas;
    this.settings.colHeaders = [...this.headers];
    if (this.columna_extra) {
      this.settings.colHeaders.push(' ');
      this.settings.stretchH = 'last';
    }
    if (this.fila_totalizadora) {
      this.settings.className += ' ca-datasheet--filaTotalizadora';
    }
    if (!this.usar_hot_columns) {
      this.actualizarSettingsColumnas();
    }

    this.$refs.ca_datasheet.hotInstance.render(); // Renderización inicial
  },
  methods: {
    /**
     * Invocado para actualizar settings de HT cuando no se usa hot columns.
     */
    actualizarSettingsColumnas() {
      this.settings.columns = this.configuracionesHT;
    },
  },
};
</script>
<style src="../../node_modules/handsontable/dist/handsontable.min.css"></style>
<style lang="scss">
//@import "variables";
.ca-datasheet {
  .wtHolder {
    width: fit-content !important;
  }
  // Estilización cuando se realiza doble click sobre una celda para editar directamente
  // WARNING: Si se quisiese mayor estilización (por ejemplo dar más aire a la derecha, o escribir sobre izquierda,
  //            se deben redefinir los estilos en el componente que este utilizando la tabla).
  .handsontableInputHolder {
    z-index: 1;
    height: 26px; // 20px (replicado en .handsontableInput) + 6 de padding para texto centrado. ToDo mejorar en lo posible
    padding-top: 6px;
    background-color: color(blanco);
  }
  textarea.handsontableInput {
    box-shadow: unset;
    text-align: end;
    height: 20px !important;
  }

  // Bordes de celdas seleccionadas
  .wtBorder.current {
    background-color: color(celeste) !important;
    z-index: 2;
  }

  // Header Tabla
  th {
    border: none !important; // ToDo revisar cdo es necesario y cdo no
    background-color: color(gris-oscuro);
    color: color(blanco);

    .colHeader {
      padding: 12px 0;
    }

    &:first-child {
      border-top-left-radius: 2px;
    }
    &:last-child {
      border-top-right-radius: 2px;
    }
    &.ht__highlight {
      // Header columna seleccionada
      background-color: color(texto-oscuro) !important;
      color: color(blanco) !important;
    }
  }

  // Body Tabla
  tbody {
    td {
      vertical-align: middle;
      border: none; // corrección css heredado de handsontable
    }
    // Filas de color alternado
    tr:nth-child(even) td {
      background-color: color(fondo-gris-claro) !important;
    }
    tr:nth-child(odd) td {
      background-color: color(blanco) !important;
    }
  }

  // Estilización columnas fijadas a la izquierda
  .ht_clone_left td:last-child {
    border-right: 2px solid color(gris);
  }

  // Estilización fila fijada inferior
  .ht_clone_bottom tr:last-child {
    td:last-child {
      background-color: color(dark) !important;
    }
  }

  // Las filas/columnas fijadas tienen preponderancia sobre la tabla para dibujarse.
  .ht_clone_top,
  .ht_clone_top_left_corner,
  .ht_clone_bottom_left_corner,
  .ht_clone_left {
    z-index: 3;
  }

  // Modificadores generales de tabla (globales)
  &.ca-datasheet--filaTotalizadora {
    .ht_master tr:last-child,
    .ht_clone_left tr:last-child {
      height: 0 !important;
      td {
        height: 0 !important;
        display: none;
      }
    }
  }

  // Estilización celdas de la tabla, primero de manera general y luego modificadores configurados en ConfiguracionColumna
  .ca-datasheet-celda {
    color: color(gris);
    padding: 0;
    font-size: 13px;
    .celda-box {
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 2px;
      border: 1px solid color(celeste);
      background-color: color(blanco);
      color: color(dark);
      overflow: hidden;
      text-overflow: ellipsis;

      /**
            Centrado de celda: 8px a izquierda que definen el margen deseado, luego el ancho se calcula
              en base al 100% disponible, que pasa a ser:
                Ancho de columna (100% original)
                + 8px

              Es necesario para poder simular el margen derecho por estar dentro de una tabla.
         */
      margin-left: 8px;
      padding: 0 5px;
      width: calc(100% - 30px);
    }
    &.current.highlight {
      .celda-box {
        background-color: transparent;
        border: unset;
        color: color(celeste);
      }
    }
  }

  // Alineamiento: Funciona tanto para celdas readonly (uso de text-align) como para las editables (justify de flex celda-box)
  .ca-datasheet-celda--center {
    text-align: center;
    .celda-box {
      justify-content: center;
    }
  }
  .ca-datasheet-celda--left {
    text-align: left;
    .celda-box {
      justify-content: flex-start;
    }
  }
  .ca-datasheet-celda--right {
    text-align: end;
    .celda-box {
      justify-content: flex-end;
    }
  }

  // Estilización de celda-box (recuadro que cubre input modificable)
  .ca-datasheet-celda--placeholder .celda-box {
    // Cuando en la celda figura el placeholder en vez del valor se estiliza grisada
    color: color(celeste-claro);
    background-color: color(gris-claro);
    border-color: color(celeste-claro);
  }

  .ca-datasheet-celda--disabled .celda-box {
    background-color: color(fondo-deshabilitado);
    color: color(gris-deshabilitado);
    border: color(gris-deshabilitado);
  }

  .ca-datasheet-celda--invalid .celda-box {
    border-color: color(rojo);
    color: color(rojo);
  }
}
#container-ca-datasheet {
  .v-progress-linear {
    margin: 0;

    .primary {
      background-color: color(naranja) !important;
    }
  }
}
</style>
